import { useState, useEffect, useMemo, useCallback } from 'react';
import { Pencil, ChevronDown } from 'lucide-react';
import { api } from '../api/client';
import { BareTextarea } from '../components/ProjectForm';
import { useT } from '../i18n';
import type { DeliverableRow, GridColumn } from '../types';

export function DeliverablesPage() {
  const { t } = useT();
  const [deliverables, setDeliverables] = useState<DeliverableRow[]>([]);
  const [statuses, setStatuses] = useState<GridColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null); // "projectId-index"
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    const [d, s] = await Promise.all([
      api.getInventoryDeliverables() as Promise<DeliverableRow[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
    ]);
    setDeliverables(d);
    setStatuses(s);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const keyOf = (d: DeliverableRow) => `${d.projectId}-${d.deliverableIndex}`;

  const toggleExpand = (key: string) => setExpanded(s => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const startEdit = (d: DeliverableRow) => {
    const key = keyOf(d);
    setEditingKey(key);
    setEditTitle(d.title);
    setEditBody(d.body);
    setExpanded(s => new Set(s).add(key));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditTitle('');
    setEditBody('');
  };

  const saveEdit = async (d: DeliverableRow) => {
    if (!editTitle.trim()) return;
    try {
      await api.updateDeliverable(d.projectId, d.deliverableIndex, editTitle.trim(), editBody);
      cancelEdit();
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const filtered = useMemo(() => {
    let result = deliverables;

    if (statusFilter) {
      result = result.filter(d => d.statusLabel === statusFilter);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.body.toLowerCase().includes(q) ||
        d.projectName.toLowerCase().includes(q) ||
        d.sponsor.toLowerCase().includes(q) ||
        d.collaborators.some(c => c.name.toLowerCase().includes(q))
      );
    }

    return result;
  }, [deliverables, statusFilter, search]);

  // Count deliverables per status for chip labels
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const d of deliverables) {
      counts[d.statusLabel] = (counts[d.statusLabel] || 0) + 1;
    }
    return counts;
  }, [deliverables]);

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('deliverables.title')}</h1>
          <p className="page-meta">{filtered.length === 1 ? t('deliverables.countOne', { n: filtered.length }) : t('deliverables.countMany', { n: filtered.length })}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="filter-chips">
          <button
            className={`chip ${statusFilter === '' ? 'active' : ''}`}
            onClick={() => setStatusFilter('')}
          >
            {t('deliverables.allCount', { n: deliverables.length })}
          </button>
          {statuses.map(s => (
            <button
              key={s.id}
              className={`chip ${statusFilter === s.label ? 'active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === s.label ? '' : s.label)}
            >
              {s.label} ({statusCounts[s.label] || 0})
            </button>
          ))}
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('deliverables.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('deliverables.emptyTitle')}</div>
          <div className="empty-state-desc">{search || statusFilter ? t('deliverables.emptyDescFilter') : t('deliverables.emptyDescDefault')}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {filtered.map((d) => {
            const key = keyOf(d);
            const isEditing = editingKey === key;
            const isOpen = isEditing || expanded.has(key);

            return (
              <div className="deliv-card" key={key}>
                <div className="deliv-card-head">
                  <button type="button" className="deliv-fold" onClick={() => toggleExpand(key)} title={isOpen ? t('deliverables.collapse') : t('deliverables.expand')}>
                    <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                  </button>
                  {isEditing ? (
                    <input
                      className="form-input--bare"
                      style={{ fontWeight: 600 }}
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium text-ink-1" style={{ flex: 1, cursor: 'pointer' }} onClick={() => toggleExpand(key)}>{d.title}</span>
                  )}
                  <a href={`#project?id=${d.projectId}`} className="badge badge-accent">{d.projectName}</a>
                  {d.statusLabel && <span className="badge">{d.statusLabel}</span>}
                  {!isEditing && (
                    <button className="btn-icon" title={t('common.edit')} onClick={() => startEdit(d)}>
                      <Pencil className="icon-sm" />
                    </button>
                  )}
                </div>

                {isOpen && (
                  <>
                    {isEditing ? (
                      <BareTextarea value={editBody} onChange={setEditBody} placeholder={t('deliverables.bodyPlaceholder')} />
                    ) : (
                      <div className="form-input--bare" style={{ cursor: 'default', whiteSpace: 'pre-wrap' }}>{d.body || '—'}</div>
                    )}
                    <div className="flex gap-8 items-center">
                      <span className="text-sm text-ink-3">{d.sponsor || '—'}</span>
                      {d.collaborators.length > 0 && d.collaborators.map((c, j) => (
                        <span key={j} className="badge">
                          {c.name}{c.allocation > 0 ? ` ${c.allocation}%` : ''}
                        </span>
                      ))}
                    </div>
                    {isEditing && (
                      <div className="flex gap-8">
                        <button className="btn btn-accent btn-sm" onClick={() => saveEdit(d)} disabled={!editTitle.trim()}>{t('common.save')}</button>
                        <button className="btn btn-ghost btn-sm" onClick={cancelEdit}>{t('common.cancel')}</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
